// Imports
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { z } from 'zod';
import ButtonGroup from '../../../shareables/foundation/front-end/components/buttons/group';
import Button from '../../../shareables/foundation/front-end/components/buttons/standard';
import { useAppDispatch, useAppSelector } from '../../../shareables/foundation/front-end/redux/hooks';
import APIButton, { APIButtonOptions } from '../../../shareables/integrations/api/components/button';
import { GetStartResponse } from '../../../shareables/types/api/main/start/get';
import apiActions from '../../../shareables/integrations/api/redux/actions';
import AnchorButton from '../../../shareables/foundation/front-end/components/buttons/anchor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { CompleteWebsiteParameters } from '../../../shareables/types/api/main/start/website/complete';


// Styled components
const Heading = styled.h2`
	text-align: center;
	font-size: 1.4rem;
	font-weight: 400;
	margin-bottom: 0.625rem;
	color: hsl(208, 25%, 24%);
	line-height: 1;
	
	outline: 0;
`;

const OnboardingCallBenefits = styled.ul`
	list-style: none;
	line-height: 1.3;
	
	display: block;
	text-align: center;
	margin-top: 0.05rem;
	margin-bottom: 1.25rem;
	
	> li > svg {
		color: hsl(96, 38%, 53%);
	}
`;

const Instructions = styled.p`
	line-height: 1.3;
	text-align: center;
	
	&:not(:last-child) {
		margin-bottom: 1.25rem;
	}
`;


// Define the accepted props
interface Props {
	startState: GetStartResponse;
	migrate: boolean;
	storybookOnboardingCallScheduled?: boolean;
	storybookLoadingCompletion?: boolean;
	storybookCompleted?: boolean;
}


// Function component
const Finish: React.FC<Props> = ({
	startState,
	migrate,
	storybookOnboardingCallScheduled,
	storybookLoadingCompletion,
	storybookCompleted,
}) => {
	// Use state
	const [onboardingCallScheduled, setOnboardingCallScheduled] = useState(storybookOnboardingCallScheduled ?? false);
	const [loadingCompletion, setLoadingCompletion] = useState(storybookLoadingCompletion ?? false);
	const [completed, setCompleted] = useState(storybookCompleted ?? false);
	
	
	// Use ref
	const headingRef = useRef<HTMLHeadingElement>(null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const user = useAppSelector((state) => state.common.user);
	
	
	// Build Plausible events
	const plausibleEvents = {
		onboardingCallScheduled: 'Scheduled onboarding call',
		completed: 'Completed start process',
	};
	
	
	// Hook into Calendly's events
	useEffect(() => {
		const goForward = (e: MessageEvent<any>) => {
			const eventScheduledSchema = z.object({
				data: z.object({
					event: z.literal('calendly.event_scheduled'),
				}),
			});
			
			if (eventScheduledSchema.safeParse(e).success) {
				setOnboardingCallScheduled(true);
				
				window.plausible(plausibleEvents.onboardingCallScheduled);
				
				setLoadingCompletion(true);
				
				void dispatch(
					apiActions.call<CompleteWebsiteParameters, undefined, undefined>({
						action: 'COMPLETE',
						uri: `/start/website/${startState?.websiteID ?? ''}`,
						callBeforeFinish: () => setLoadingCompletion(false),
						parameters: {
							scheduledOnboardingCall: true,
						},
						completion: () => {
							window.plausible(plausibleEvents.completed);
							
							setCompleted(true);
						},
					})
				);
			}
		};
		
		window.addEventListener('message', goForward);
		
		return () => {
			window.removeEventListener('message', goForward);
		};
	}, [dispatch, startState?.websiteID, plausibleEvents.onboardingCallScheduled, plausibleEvents.completed]);
	
	
	// Move focus to the heading on mount
	useEffect(() => {
		headingRef.current?.focus();
	}, []);
	
	
	// Build button options
	const buttonOptions: APIButtonOptions<CompleteWebsiteParameters, undefined, undefined> = {
		action: 'COMPLETE',
		uri: `/start/website/${startState?.websiteID ?? ''}`,
	};
	
	
	// Build onboarding call benefits
	const onboardingCallBenefits: string[] = [
		'Learn need-to-know concepts',
		'Configure important settings',
		'See our favorite features',
		'Ask us any questions',
	];
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Heading tabIndex={-1} ref={headingRef}>
				{migrate ? 'We’re on it' : onboardingCallScheduled ? 'Thanks for scheduling' : 'You’re off to a great start'}!
			</Heading>
			
			<Instructions>
				{migrate
					? 'A customer success champion will start working on your migration soon! If we have any questions along the way, we’ll reach out via email. We typically complete migrations in about a week.'
					: onboardingCallScheduled
						? 'It’s time to visit the Admin Portal to continue building your community’s new website.'
						: 'Next, we’ll take you to the Admin Portal to continue building your community’s new website. But first, let’s schedule an onboarding call with one of our knowledgeable Customer Success Champions.'}
			</Instructions>
			
			{!migrate && (
				<React.Fragment>
					{!onboardingCallScheduled && (
						<OnboardingCallBenefits>
							{onboardingCallBenefits.map((benefit, i) => (
								<li key={i}>
									<FontAwesomeIcon icon={faCheck} /> {benefit}
								</li>
							))}
						</OnboardingCallBenefits>
					)}
					
					<ButtonGroup>
						{!onboardingCallScheduled && (
							<Button
								color='accent'
								type='button'
								onClick={() =>
									window.Calendly?.initPopupWidget({
										url: 'https://calendly.com/hoa-express/onboarding-call',
										prefill: {
											name: user.name ?? '',
											email: user.email ?? '',
											customAnswers: {
												a1: startState.communityName ?? '',
											},
										},
									})
								}
							>
								Schedule onboarding call
							</Button>
						)}
						
						{onboardingCallScheduled && (completed || loadingCompletion) ? (
							<AnchorButton
								href={`https://admin.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/dashboard?websiteID=${
									startState.websiteID ?? ''
								}`}
								color='accent'
								isLoading={loadingCompletion}
							>
								Continue
							</AnchorButton>
						) : (
							<APIButton
								options={buttonOptions}
								color={onboardingCallScheduled ? 'accent' : 'plain'}
								onCompletion={() => {
									if (window.plausibleLoaded) {
										window.plausible(plausibleEvents.completed, {
											callback: () => {
												window.location.href = `https://admin.${
													process.env.REACT_APP__ENVIRONMENT_HOSTNAME
												}/dashboard?websiteID=${startState.websiteID ?? ''}`;
											},
										});
									} else {
										window.location.href = `https://admin.${
											process.env.REACT_APP__ENVIRONMENT_HOSTNAME
										}/dashboard?websiteID=${startState.websiteID ?? ''}`;
									}
								}}
								border={!onboardingCallScheduled}
								parameters={{
									scheduledOnboardingCall: onboardingCallScheduled,
								}}
							>
								{onboardingCallScheduled ? 'Continue' : 'Skip for now'}
							</APIButton>
						)}
					</ButtonGroup>
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

export default Finish;
